import { faCalendar, faCancel, faShare, faUser } from '@fortawesome/pro-light-svg-icons';

import { AppointmentOptionButtonDownload } from './appointment-option-button-download.component';

import { AppointmentOptionsModalKeys } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/variants/_components/appointment-options/appointment-options-button.component';
import { Button } from '@/components/ui/button/button.component';
import { AppointmentCancelStatus, AppointmentsLifecycleEnum } from '@/types/enums/appointments.enum';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { Appointment } from '@/types/appointments.types';
import { useToast } from '@/components/ui/use-toast';

interface AppointmentOptionsDefaultProps {
  appointment: Appointment;
  lifecycle: AppointmentsLifecycleEnum;
}

const tabsDictionary: Record<AppointmentsLifecycleEnum, string[]> = {
  [AppointmentsLifecycleEnum.FUTURE]: [
    'DEFAULT',
    'SHARE',
    'CHANGE_PATIENT_LIST',
    'CHANGE_DATE',
    'REQUEST_CANCELLATION',
  ],
  [AppointmentsLifecycleEnum.PAST]: ['DEFAULT', 'SHARE'],
  [AppointmentsLifecycleEnum.OTHER]: ['DEFAULT', 'SHARE', 'CHANGE_PATIENT_LIST', 'REQUEST_CANCELLATION'],
};

export function AppointmentOptionsDefault({ appointment, lifecycle }: AppointmentOptionsDefaultProps) {
  const { toast } = useToast();
  const { handleJump: jump } = useProgressiveModal();

  const tabs = tabsDictionary[lifecycle];

  const isFourHoursBeforeAppointment = (): boolean => {
    const fourHours = 4 * 60 * 60 * 1000;
    const appointmentDate = new Date(appointment.start);

    const timelimit = appointmentDate.getTime() - fourHours;
    const nowtime = new Date().getTime();

    return !(timelimit > nowtime);
  };

  const onClickChangePatient = (): void => {
    if (isFourHoursBeforeAppointment()) {
      toast.error('As solicitações de troca de paciente devem ser feitas até 4 horas antes da consulta ou exame.');
    } else if (appointment.requestCancelStatus === AppointmentCancelStatus.PENDING) {
      toast.error('Esse agendamento está em processo de cancelamento.');
    } else {
      jump({ key: AppointmentOptionsModalKeys.CHANGE_PATIENT_LIST });
    }
  };

  const onClickCancel = (): void => {
    if (isFourHoursBeforeAppointment()) {
      toast.error('As solicitações de cancelamento devem ser feitas até 4 horas antes da consulta ou exame.');
    } else if (appointment.requestCancelStatus === AppointmentCancelStatus.PENDING) {
      toast.error('Esse agendamento está em processo de cancelamento.');
    } else if (appointment.cancelable) {
      jump({ key: AppointmentOptionsModalKeys.REQUEST_CANCELLATION });
    }
  };

  const onClickChangeDate = (): void => {
    if (isFourHoursBeforeAppointment()) {
      toast.error('As solicitações de alteração de horário devem ser feitas em até 4 horas antes da consulta ou exame');
    } else if (appointment.requestCancelStatus === AppointmentCancelStatus.PENDING) {
      toast.error('Esse agendamento está em processo de cancelamento.');
    } else {
      jump({ key: AppointmentOptionsModalKeys.CHANGE_DATE });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {tabs.includes(AppointmentOptionsModalKeys.SHARE) && (
        <Button
          data-testid="button-share--appointment-options"
          className="flex justify-start"
          startIcon={faShare}
          variant="text"
          onClick={() => jump({ key: AppointmentOptionsModalKeys.SHARE })}
        >
          Compartilhar
        </Button>
      )}
      <AppointmentOptionButtonDownload id={appointment.id} />
      {tabs.includes(AppointmentOptionsModalKeys.CHANGE_PATIENT_LIST) && (
        <Button
          data-testid="button-change-patient--appointment-options"
          className="flex justify-start"
          startIcon={faUser}
          variant="text"
          onClick={onClickChangePatient}
        >
          Trocar paciente
        </Button>
      )}
      {tabs.includes(AppointmentOptionsModalKeys.CHANGE_DATE) && (
        <Button
          data-testid="button-change-date-time--appointment-options"
          className="flex justify-start"
          startIcon={faCalendar}
          variant="text"
          onClick={onClickChangeDate}
        >
          Trocar data ou hora
        </Button>
      )}
      {tabs.includes(AppointmentOptionsModalKeys.REQUEST_CANCELLATION) && (
        <Button
          data-testid="button-request-cancellation--appointment-options"
          className="flex justify-start"
          color="danger"
          startIcon={faCancel}
          variant="text"
          onClick={onClickCancel}
        >
          Solicitar cancelamento
        </Button>
      )}
    </div>
  );
}
